/**
 * useCompanionRtc — Welle 8c Phase A + B
 *
 * Verbindung der Companion-Session (zweite Berater-Session, KEIN A/V) zur Stele.
 * Der Companion ist die OFFERTE Seite: eigene PeerConnection + Steuerkanal.
 *
 * Phase A: Steuerkanal (DTLS-P2P).
 * Phase B: Bildschirmfreigabe — getDisplayMedia-Track wird über dieselbe
 *   PeerConnection per Renegotiation an die Stele gegeben. Weil das ein echter
 *   Medien-Track ist, bekommt er einen EIGENEN SFrame-Handshake (eigener Schlüssel,
 *   ausgetauscht über den Steuerkanal — nie über den Server). Die A/V-E2EE bleibt
 *   davon unberührt; der Companion zählt serverseitig nicht als Medien-Teilnehmer.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { loadIceServers } from '../lib/iceServers';
import { io, Socket } from 'socket.io-client';
import { createE2eeSession, applySframe, sframeSupported, e2eeForced, E2eeSession } from './sframe';
import { SCAN_CHUNK_SIZE } from './camControl';
import type { SignatureResult } from './useWebRtc';

export type CompanionStatus =
  | 'idle' | 'connecting' | 'waiting-peer' | 'negotiating' | 'connected' | 'error' | 'ended';

export interface CompanionSnapshot {
  url: string;        // ObjectURL für <img> / Download
  format: string;     // jpeg | png | webp
  bytes: number;
  role: 'main' | 'preview';
  at: number;
}

export interface CompanionRtc {
  status: CompanionStatus;
  error?: string;
  /** true, sobald der P2P-Steuerkanal zur Stele offen ist */
  channelOpen: boolean;
  /** true, während der Companion seinen Bildschirm teilt */
  screenSharing: boolean;
  /** Bildschirmfreigabe starten; false bei Abbruch/Fehler */
  startScreenShare: () => Promise<boolean>;
  /** Bildschirmfreigabe beenden */
  stopScreenShare: () => Promise<void>;
  /** Steuerbefehl P2P (DTLS) an die Stele senden; false wenn der Kanal nicht offen ist */
  send: (msg: Record<string, unknown>) => boolean;
  /** Welle 8c-pre19 (Phase C): hochauflösenden Scan/Snapshot der Stele anfordern */
  requestScan: (role: 'main' | 'preview', format?: 'jpeg' | 'png' | 'webp') => boolean;
  /** true, während ein Scan läuft (Anforderung gesendet, Bild noch nicht vollständig) */
  scanning: boolean;
  /** zuletzt empfangener Snapshot (ObjectURL); null wenn noch keiner */
  lastSnapshot: CompanionSnapshot | null;
  /** letzte Scan-Fehlermeldung (z.B. Kamera nicht vorhanden) */
  scanError: string | null;
  /** Welle 12 (pre117): Dokumentenlicht der Stele schalten (Relais 1) */
  setLight: (on: boolean) => boolean;
  /** zuletzt gemeldeter Lichtzustand */
  lastLight: { on: boolean; ok: boolean } | null;
  /** ROI/Zoom-Steuerung auf dem Master-Bild der Stele (nur Steuerung, kein Inspection-Stream) */
  roiSet: (role: 'main' | 'preview', roi: { x: number; y: number; width: number; height: number }) => boolean;
  roiMove: (role: 'main' | 'preview', dx: number, dy: number) => boolean;
  roiZoom: (role: 'main' | 'preview', factor: number, anchor?: { x: number; y: number }) => boolean;
  roiReset: (role: 'main' | 'preview') => boolean;
  /** Welle 12 (pre127): PDF über den Companion-Kanal an die Stele zum Signieren schicken (gechunkt, mit Quittung). */
  submitSignature: (file: File) => Promise<string | null>;
  /** laufende Signatur abbrechen */
  abortSignature: () => void;
  /** Status/Ergebnis der letzten Signatur (signiertes PDF als ObjectURL bei 'received') */
  lastSignature: SignatureResult | null;
}

type PeerEntry = { peerId: string; role?: string; companion?: boolean } | string;
function pickStelePeerId(peers: unknown): string | null {
  if (!Array.isArray(peers)) return null;
  for (const p of peers as PeerEntry[]) {
    if (typeof p === 'string') return p;
    if (p && p.role === 'stele') return p.peerId;
  }
  return null;
}

export function useCompanionRtc(rtcToken: string | null, e2ee = false): CompanionRtc {
  const [status, setStatus] = useState<CompanionStatus>('idle');
  const [error, setError] = useState<string | undefined>(undefined);
  const [channelOpen, setChannelOpen] = useState(false);
  const [screenSharing, setScreenSharing] = useState(false);
  // Welle 8c-pre19 (Phase C): Scan/Snapshot-Empfang über den Steuerkanal
  const [scanning, setScanning] = useState(false);
  const [lastSnapshot, setLastSnapshot] = useState<CompanionSnapshot | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const [lastLight, setLastLight] = useState<{ on: boolean; ok: boolean } | null>(null);
  const [lastSignature, setLastSignature] = useState<SignatureResult | null>(null);
  const sigBufRef = useRef<{ sigId: string; filename?: string; totalBytes: number; chunks: ArrayBuffer[]; receivedBytes: number } | null>(null);
  const sigSubmitAckRef = useRef<Map<string, () => void>>(new Map());
  const lastSigUrlRef = useRef<string | null>(null);
  const lastSigFilenameRef = useRef<string | null>(null);
  const sigCurrentIdRef = useRef<string | null>(null);
  const scanBufRef = useRef<{ role: 'main' | 'preview'; format: string; totalBytes: number; chunks: ArrayBuffer[]; received: number } | null>(null);
  const lastSnapshotUrlRef = useRef<string | null>(null);

  const socketRef = useRef<Socket | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const peerIdRef = useRef<string | null>(null);
  const pendingIceRef = useRef<RTCIceCandidateInit[]>([]);
  const offeredRef = useRef(false);
  const negotiatingRef = useRef(false);
  // Phase B: Screen-Track + eigene SFrame-Session
  const screenStreamRef = useRef<MediaStream | null>(null);
  const screenSenderRef = useRef<RTCRtpSender | null>(null);
  const screenE2eeRef = useRef<E2eeSession | null>(null);

  // Effektives E2EE: Test-Override (?e2ee=) hat Vorrang, sonst Tenant-Policy aus Attach.
  const e2eeEffectiveRef = useRef<boolean>(false);
  useEffect(() => { e2eeEffectiveRef.current = e2eeForced() ?? e2ee; }, [e2ee]);

  const dcSend = useCallback((msg: Record<string, unknown>): boolean => {
    const dc = dcRef.current;
    if (!dc || dc.readyState !== 'open') return false;
    try { dc.send(JSON.stringify(msg)); return true; } catch { return false; }
  }, []);

  const renegotiate = useCallback(async () => {
    const pc = pcRef.current;
    const peerId = peerIdRef.current;
    if (!pc || !peerId || negotiatingRef.current) return;
    negotiatingRef.current = true;
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      socketRef.current?.emit('rtc:offer', { sdp: pc.localDescription, to: peerId });
      console.log('[companion] Renegotiation-Offer gesendet');
    } catch (e) {
      console.warn('[companion] Renegotiation fehlgeschlagen', e);
    } finally {
      negotiatingRef.current = false;
    }
  }, []);

  const stopScreenShare = useCallback(async () => {
    dcSend({ type: 'companion.share-state', active: false });
    try { screenStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    screenStreamRef.current = null;
    const pc = pcRef.current;
    const sender = screenSenderRef.current;
    if (pc && sender) { try { pc.removeTrack(sender); } catch { /* ignore */ } }
    screenSenderRef.current = null;
    screenE2eeRef.current = null;
    setScreenSharing(false);
    await renegotiate();
    console.log('[companion] Bildschirmfreigabe beendet');
  }, [dcSend, renegotiate]);

  const startScreenShare = useCallback(async (): Promise<boolean> => {
    const pc = pcRef.current;
    if (!pc || screenSenderRef.current) return false;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
    } catch {
      return false; // Nutzer hat die Auswahl abgebrochen
    }
    const track = stream.getVideoTracks()[0];
    if (!track) { try { stream.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ } return false; }
    screenStreamRef.current = stream;

    // Eigener SFrame-Handshake für den Screen-Track (nur wenn E2EE aktiv & unterstützt).
    if (e2eeEffectiveRef.current && sframeSupported()) {
      try {
        const sess = await createE2eeSession('companion-screen');
        screenE2eeRef.current = sess;
        dcSend({ type: 'companion.e2ee-key', publicKey: await sess.exportPublicKey() });
      } catch (e) {
        console.warn('[companion] Screen-E2EE-Setup fehlgeschlagen — Fallback DTLS', e);
        screenE2eeRef.current = null;
      }
    }

    const sender = pc.addTrack(track, stream);
    screenSenderRef.current = sender;
    if (screenE2eeRef.current) applySframe(sender, screenE2eeRef.current, 'encrypt');

    track.onended = () => { void stopScreenShare(); }; // Stop über Browser-UI

    await renegotiate();
    dcSend({ type: 'companion.share-state', active: true });
    setScreenSharing(true);
    console.log('[companion] Bildschirmfreigabe gestartet (e2ee=', !!screenE2eeRef.current, ')');
    return true;
  }, [dcSend, renegotiate, stopScreenShare]);

  useEffect(() => {
    if (!rtcToken) return;
    let cancelled = false;
    setStatus('connecting');
    setError(undefined);
    setChannelOpen(false);
    offeredRef.current = false;

    (async () => {
      const iceServers: RTCIceServer[] = await loadIceServers();
      if (cancelled) return;

      // Insertable Streams schon bei PC-Erstellung aktivieren (wird für SFrame auf dem
      // später hinzugefügten Screen-Track gebraucht). Harmlos, wenn ungenutzt.
      const pcConfig: RTCConfiguration = { iceServers };
      if (sframeSupported()) (pcConfig as any).encodedInsertableStreams = true;
      const pc = new RTCPeerConnection(pcConfig);
      pcRef.current = pc;

      pc.onicecandidate = (e) => {
        if (e.candidate && peerIdRef.current) {
          socketRef.current?.emit('rtc:ice', { candidate: e.candidate, to: peerIdRef.current });
        }
      };
      pc.onconnectionstatechange = () => {
        const st = pc.connectionState;
        console.log('[companion] pc state', st);
        if (st === 'connected') setStatus('connected');
        else if (st === 'failed') { setStatus('error'); setError('Verbindung fehlgeschlagen'); }
        else if (st === 'disconnected' || st === 'closed') setStatus((s) => (s === 'connected' ? 'ended' : s));
      };

      const dc = pc.createDataChannel('bamoa-companion-ctrl', { ordered: true });
      dcRef.current = dc;
      dc.binaryType = 'arraybuffer'; // Welle 8c-pre19: Scan-Chunks binär empfangen
      dc.onopen = () => {
        console.log('[companion] Steuerkanal offen');
        setChannelOpen(true);
        try { dc.send(JSON.stringify({ type: 'companion.hello', from: 'companion' })); } catch { /* ignore */ }
      };
      dc.onclose = () => { console.log('[companion] Steuerkanal geschlossen'); setChannelOpen(false); };
      dc.onerror = (err) => console.warn('[companion] dc error', err);
      dc.onmessage = async (ev) => {
        // Welle 8c-pre19: Binäre Daten = Scan-Chunk. Sammeln bis scan.end.
        if (typeof ev.data !== 'string') {
          const buf: ArrayBuffer = ev.data instanceof ArrayBuffer ? ev.data : await (ev.data as Blob).arrayBuffer();
          const sg = sigBufRef.current;
          if (sg) {
            sg.chunks.push(buf); sg.receivedBytes += buf.byteLength;
            if (sg.totalBytes > 0) {
              const pct = Math.min(100, Math.round((sg.receivedBytes / sg.totalBytes) * 100));
              setLastSignature((prev) => (prev && prev.sigId === sg.sigId && prev.progress !== pct ? { ...prev, progress: pct } : prev));
            }
            return;
          }
          const sb = scanBufRef.current;
          if (sb) { sb.chunks.push(buf); sb.received += buf.byteLength; }
          return;
        }
        let m: any;
        try { m = JSON.parse(ev.data); } catch { return; }
        console.log('[companion] msg', m?.type);
        // Phase B: SFrame-Handshake für den Screen-Track (über den Steuerkanal).
        if (m?.type === 'companion.e2ee-key' && m.publicKey) {
          const sess = screenE2eeRef.current;
          if (sess) { await sess.onPeerKey(m.publicKey); dcSend({ type: 'companion.e2ee-ready' }); }
          return;
        }
        if (m?.type === 'companion.e2ee-ready') { screenE2eeRef.current?.markPeerReady(); return; }
        // Welle 8c-pre19 (Phase C): hochauflösender Scan/Snapshot von der Stele.
        if (m?.type === 'camera.scan.start') {
          scanBufRef.current = { role: m.role, format: m.format, totalBytes: m.totalBytes, chunks: [], received: 0 };
          setScanError(null);
          setScanning(true);
          return;
        }
        if (m?.type === 'camera.scan.end') {
          const sb = scanBufRef.current;
          scanBufRef.current = null;
          setScanning(false);
          if (!sb) return;
          const mime = sb.format === 'png' ? 'image/png' : sb.format === 'webp' ? 'image/webp' : 'image/jpeg';
          const blob = new Blob(sb.chunks, { type: mime });
          if (lastSnapshotUrlRef.current) { try { URL.revokeObjectURL(lastSnapshotUrlRef.current); } catch { /* ignore */ } }
          const url = URL.createObjectURL(blob);
          lastSnapshotUrlRef.current = url;
          setLastSnapshot({ url, format: sb.format, bytes: blob.size, role: sb.role, at: Date.now() });
          console.log(`[companion] Snapshot empfangen ${(blob.size / 1024).toFixed(1)} KB (${sb.role})`);
          return;
        }
        if (m?.type === 'camera.scan.error') {
          scanBufRef.current = null;
          setScanning(false);
          setScanError(m.error === 'camera-not-found' ? 'Kamera nicht verfügbar' : (m.error ?? 'Scan fehlgeschlagen'));
          return;
        }
        if (m?.type === 'light.state') { setLastLight({ on: !!m.on, ok: m.ok !== false }); return; }
        // Welle 12 (pre130): EC-Karten-/NFC-Ereignis von der Stele (P2P) → Window-Event,
        // der Companion baut daraus das EC-Protokoll und legt es ab.
        if (m?.type === 'nfc') { window.dispatchEvent(new CustomEvent('bamoa:nfc', { detail: m.payload })); return; }
        // Welle 12 (pre127): Signatur-Quittungen & Rückübertragung des signierten PDF.
        if (m?.type === 'sign.received') {
          setLastSignature((prev) => ({ sigId: m.sigId, status: 'submitted', filename: prev && prev.sigId === m.sigId ? prev.filename : lastSigFilenameRef.current ?? undefined, receivedAt: Date.now() }));
          return;
        }
        if (m?.type === 'sign.signing') {
          setLastSignature((prev) => ({ sigId: m.sigId, status: 'signing', filename: prev && prev.sigId === m.sigId ? prev.filename : lastSigFilenameRef.current ?? undefined, receivedAt: Date.now() }));
          return;
        }
        if (m?.type === 'sign.submit.ack') {
          const r = sigSubmitAckRef.current.get(m.sigId);
          if (r) { r(); sigSubmitAckRef.current.delete(m.sigId); }
          return;
        }
        if (m?.type === 'sign.result.start') {
          sigBufRef.current = { sigId: m.sigId, filename: m.filename, totalBytes: m.totalBytes, chunks: [], receivedBytes: 0 };
          setLastSignature((prev) => (prev && prev.sigId === m.sigId ? { ...prev, status: 'signing', progress: 0 } : prev));
          return;
        }
        if (m?.type === 'sign.result.end') {
          const sg = sigBufRef.current;
          if (!sg || sg.sigId !== m.sigId) return;
          if (sg.receivedBytes < sg.totalBytes) { sigBufRef.current = null; return; }
          const blob = new Blob(sg.chunks, { type: 'application/pdf' });
          if (lastSigUrlRef.current) { try { URL.revokeObjectURL(lastSigUrlRef.current); } catch { /* */ } }
          const url = URL.createObjectURL(blob);
          lastSigUrlRef.current = url;
          setLastSignature({ sigId: sg.sigId, status: 'received', url, filename: sg.filename, progress: 100, receivedAt: Date.now() });
          sigBufRef.current = null; sigCurrentIdRef.current = null;
          dcSend({ type: 'sign.result.ack', sigId: sg.sigId });
          return;
        }
        if (m?.type === 'sign.aborted') { setLastSignature({ sigId: m.sigId, status: 'aborted', receivedAt: Date.now() }); sigBufRef.current = null; sigCurrentIdRef.current = null; return; }
        if (m?.type === 'sign.error') { setLastSignature({ sigId: m.sigId, status: 'error', error: m.error, receivedAt: Date.now() }); sigBufRef.current = null; sigCurrentIdRef.current = null; return; }
      };

      const socket = io('/rtc', { path: '/socket.io', transports: ['websocket'], auth: { token: rtcToken } });
      socketRef.current = socket;

      async function makeOffer() {
        if (offeredRef.current || !peerIdRef.current) return;
        offeredRef.current = true;
        setStatus('negotiating');
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        socket.emit('rtc:offer', { sdp: pc.localDescription, to: peerIdRef.current });
        console.log('[companion] Offer an Stele gesendet →', peerIdRef.current);
      }

      socket.on('connect_error', (e: Error) => {
        console.warn('[companion] connect_error', e?.message);
        setError(e?.message ?? 'Socket-Fehler');
        setStatus('error');
      });

      socket.on('rtc:joined', (data: { peers?: unknown }) => {
        console.log('[companion] joined', JSON.stringify(data));
        const steleId = pickStelePeerId(data?.peers);
        if (steleId) { peerIdRef.current = steleId; void makeOffer(); }
        else setStatus('waiting-peer');
      });

      socket.on('rtc:peer-joined', (data: { peerId?: string; role?: string; companion?: boolean }) => {
        console.log('[companion] peer-joined', JSON.stringify(data));
        if (data?.role === 'stele' && data.peerId) {
          peerIdRef.current = data.peerId;
          void makeOffer();
        }
      });

      socket.on('rtc:answer', async (data: { sdp: RTCSessionDescriptionInit; from?: string }) => {
        if (data.from) peerIdRef.current = data.from;
        await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
        for (const c of pendingIceRef.current) {
          await pc.addIceCandidate(new RTCIceCandidate(c)).catch(() => {});
        }
        pendingIceRef.current = [];
        console.log('[companion] Answer der Stele verarbeitet');
      });

      socket.on('rtc:ice', async (data: { candidate: RTCIceCandidateInit }) => {
        if (!pc.remoteDescription) { pendingIceRef.current.push(data.candidate); return; }
        await pc.addIceCandidate(new RTCIceCandidate(data.candidate)).catch((e) =>
          console.warn('[companion] addIceCandidate failed', e),
        );
      });

      socket.on('rtc:peer-left', () => setStatus('ended'));
      socket.on('rtc:bye', () => setStatus('ended'));
    })();

    return () => {
      cancelled = true;
      try { socketRef.current?.emit('rtc:bye'); } catch { /* ignore */ }
      try { screenStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
      try { dcRef.current?.close(); } catch { /* ignore */ }
      try { socketRef.current?.disconnect(); } catch { /* ignore */ }
      try { pcRef.current?.close(); } catch { /* ignore */ }
      socketRef.current = null;
      dcRef.current = null;
      pcRef.current = null;
      peerIdRef.current = null;
      pendingIceRef.current = [];
      screenStreamRef.current = null;
      screenSenderRef.current = null;
      screenE2eeRef.current = null;
      setScreenSharing(false);
    };
  }, [rtcToken, dcSend]);

  // Welle 12 (pre127): PDF über den Companion-Steuerkanal an die Stele signieren lassen.
  // Identisches Protokoll wie auf der A/V-Seite (sign.submit.* mit Quittung/Retry),
  // die Stele routet sign.* seit pre117 auch auf dem Companion-Kanal.
  const submitSignature = useCallback(async (file: File): Promise<string | null> => {
    const dc = dcRef.current;
    if (!dc || dc.readyState !== 'open') { console.warn('[companion] submitSignature: dc not open'); return null; }
    const sigId = `sig-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    sigCurrentIdRef.current = sigId;
    lastSigFilenameRef.current = file.name;
    setLastSignature({ sigId, status: 'uploading', filename: file.name, progress: 0, receivedAt: Date.now() });
    let buf: ArrayBuffer;
    try { buf = await file.arrayBuffer(); }
    catch (e) { console.warn('[companion] submitSignature: Datei nicht lesbar', e); setLastSignature({ sigId, status: 'error', error: 'upload-failed', filename: file.name, receivedAt: Date.now() }); return null; }
    const totalBytes = buf.byteLength;
    const totalChunks = Math.ceil(totalBytes / SCAN_CHUNK_SIZE);
    const MAX_ATTEMPTS = 5;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      const open = await (async () => {
        const t0 = Date.now();
        while (Date.now() - t0 < 30000) { if (dc.readyState === 'open') return true; await new Promise((r) => setTimeout(r, 100)); }
        return (dc.readyState as string) === 'open';
      })();
      if (!open) continue;
      const ackP = new Promise<boolean>((resolve) => {
        sigSubmitAckRef.current.set(sigId, () => resolve(true));
        window.setTimeout(() => { if (sigSubmitAckRef.current.has(sigId)) { sigSubmitAckRef.current.delete(sigId); resolve(false); } }, 15000);
      });
      let broke = false;
      try {
        dc.send(JSON.stringify({ type: 'sign.submit.start', sigId, filename: file.name, totalBytes, totalChunks, chunkSize: SCAN_CHUNK_SIZE }));
        const TARGET = 250 * 1024, THRESH = 48 * 1024, t0 = Date.now();
        let sent = 0;
        for (let i = 0; i < totalChunks; i++) {
          const s = i * SCAN_CHUNK_SIZE, en = Math.min(s + SCAN_CHUNK_SIZE, totalBytes);
          let waited = 0;
          while (dc.bufferedAmount > THRESH) { await new Promise((r) => setTimeout(r, 5)); if (dc.readyState !== 'open') { broke = true; break; } if ((waited += 5) > 60000) { broke = true; break; } }
          if (broke || dc.readyState !== 'open') { broke = true; break; }
          dc.send(buf.slice(s, en));
          sent += en - s;
          const pct = totalBytes > 0 ? Math.min(100, Math.round((sent / totalBytes) * 100)) : 100;
          setLastSignature((prev) => (prev && prev.sigId === sigId && prev.progress !== pct ? { ...prev, progress: pct } : prev));
          const ahead = (sent / TARGET) * 1000 - (Date.now() - t0);
          if (ahead > 0) await new Promise((r) => setTimeout(r, Math.min(ahead, 40)));
        }
        if (!broke) dc.send(JSON.stringify({ type: 'sign.submit.end', sigId }));
      } catch { broke = true; }
      if (broke) { sigSubmitAckRef.current.delete(sigId); continue; }
      const acked = await ackP;
      if (acked) { setLastSignature((prev) => (prev && prev.sigId === sigId ? { ...prev, status: 'submitted', progress: 100 } : prev)); return sigId; }
      console.warn(`[companion] sign.submit.ack ausgeblieben (Versuch ${attempt}/${MAX_ATTEMPTS})`);
    }
    setLastSignature({ sigId, status: 'error', error: 'upload-no-ack', filename: file.name, receivedAt: Date.now() });
    return null;
  }, []);

  const abortSignature = useCallback((): void => {
    const dc = dcRef.current;
    const sigId = sigCurrentIdRef.current;
    if (sigId && dc && dc.readyState === 'open') { try { dc.send(JSON.stringify({ type: 'sign.abort', sigId })); } catch { /* */ } }
    sigBufRef.current = null;
    setLastSignature((prev) => (prev ? { ...prev, status: 'aborted', receivedAt: Date.now() } : null));
  }, []);

  return {
    status,
    error,
    channelOpen,
    screenSharing,
    startScreenShare,
    stopScreenShare,
    send: (msg) => dcSend(msg),
    requestScan: (role, format = 'jpeg') => {
      setScanError(null);
      setScanning(true);
      const ok = dcSend({ type: 'camera.scan', role, format });
      if (!ok) { setScanning(false); setScanError('Steuerkanal nicht offen'); }
      return ok;
    },
    scanning,
    lastSnapshot,
    scanError,
    setLight: (on) => dcSend({ type: 'light.set', on }),
    lastLight,
    roiSet: (role, roi) => dcSend({ type: 'camera.roi.set', role, roi }),
    roiMove: (role, dx, dy) => dcSend({ type: 'camera.roi.move', role, dx, dy }),
    roiZoom: (role, factor, anchor) => dcSend({ type: 'camera.roi.zoom', role, factor, anchor }),
    roiReset: (role) => dcSend({ type: 'camera.roi.reset', role }),
    submitSignature,
    abortSignature,
    lastSignature,
  };
}
